import { useEffect, useState } from "react";
import { Save, Send, MessageCircle, Mail, Search, Image as ImageIcon, Upload, X, Type, LayoutGrid, Plus, Sparkles, BarChart3, AlertTriangle, Coins } from "lucide-react";
import { toast } from "sonner";
import { api, resolveImg } from "@/lib/api";
import { CATEGORY_ICON_NAMES, iconFor } from "@/lib/categoryIcons";
import { useSettings } from "@/context/SettingsContext";

const MAX_PILLARS = 6;

const TABS = [
  { id: "inicio", label: "Página inicial", icon: ImageIcon },
  { id: "contactos", label: "Contactos e email", icon: Mail },
  { id: "ia", label: "IA", icon: Sparkles },
  { id: "seo", label: "SEO", icon: Search },
  { id: "analytics", label: "Analytics", icon: BarChart3 },
];

// What an email template may refer to. Kept beside the fields that use them,
// because a placeholder nobody can remember is a placeholder nobody uses.
const PLACEHOLDERS = [
  ["{nome}", "nome completo do cliente"],
  ["{primeiro_nome}", "só o primeiro nome"],
  ["{email}", "email do cliente"],
  ["{telefone}", "telemóvel indicado"],
  ["{mensagem}", "o que o cliente escreveu"],
  ["{itens}", "lista dos produtos pedidos"],
  ["{whatsapp}", "link para o vosso WhatsApp"],
];

const Field = ({ label, ...props }) => (
  <div>
    <label className="text-sm font-semibold">{label}</label>
    <input {...props} className="w-full mt-1 rounded-2xl border border-border bg-background px-4 py-3 outline-none focus:ring-2 focus:ring-primary/40" />
  </div>
);

/** Cost in US dollars. Fractions of a cent are normal here, so 4 decimals. */
const money = (value) =>
  `US$ ${Number(value || 0).toLocaleString("pt-PT", {
    minimumFractionDigits: value < 1 ? 4 : 2,
    maximumFractionDigits: value < 1 ? 4 : 2,
  })}`;

const SpendBox = ({ label, data, since }) => (
  <div className="rounded-2xl border border-border bg-background p-4">
    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      {label}{since && <span className="normal-case font-normal"> (desde {since})</span>}
    </p>
    <p className="font-display font-extrabold text-2xl mt-1">{money(data.cost)}</p>
    <p className="text-sm text-muted-foreground mt-1">
      {data.tokens.toLocaleString("pt-PT")} tokens em{" "}
      {data.calls} {data.calls === 1 ? "chamada" : "chamadas"}
    </p>
    <p className="text-xs text-muted-foreground mt-1">
      {data.input_tokens.toLocaleString("pt-PT")} de entrada ·{" "}
      {data.output_tokens.toLocaleString("pt-PT")} de saída
    </p>
  </div>
);

const Card = ({ icon: Icon, title, desc, children, className = "" }) => (
  <div className={`bg-card rounded-3xl border border-border p-6 ${className}`}>
    <div className="flex items-center gap-2 mb-1">
      {Icon && <Icon size={18} className="text-primary" />}
      <h2 className="font-display font-bold text-lg">{title}</h2>
    </div>
    {desc && <p className="text-sm text-muted-foreground mb-4">{desc}</p>}
    <div className="space-y-4 mt-4">{children}</div>
  </div>
);

export const SettingsPanel = () => {
  const { refresh } = useSettings();
  const [tab, setTab] = useState("inicio");
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [ai, setAi] = useState(null);
  const [spend, setSpend] = useState(null);

  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)).catch(() => toast.error("Não foi possível carregar as definições.")); }, []);
  useEffect(() => { api.get("/admin/ai/status").then((r) => setAi(r.data)).catch(() => setAi(null)); }, []);
  useEffect(() => { api.get("/admin/ai/usage").then((r) => setSpend(r.data)).catch(() => setSpend(null)); }, []);

  if (!s) return <p className="text-muted-foreground">A carregar...</p>;

  const setC = (k, v) => setS((p) => ({ ...p, contacts: { ...p.contacts, [k]: v } }));
  const setSmtp = (k, v) => setS((p) => ({ ...p, smtp: { ...p.smtp, [k]: v } }));
  const setTemplate = (k, v) => setS((p) => ({ ...p, email_templates: { ...(p.email_templates || {}), [k]: v } }));
  const setAnalytics = (k, v) => setS((p) => ({ ...p, analytics: { ...(p.analytics || {}), [k]: v } }));

  const setPrices = (rows) => setS((p) => ({ ...p, ai: { ...(p.ai || {}), prices: rows } }));
  const setPrice = (i, k, v) =>
    setPrices((s.ai?.prices || []).map((row, j) => (j === i ? { ...row, [k]: v } : row)));
  const addPrice = () => setPrices([...(s.ai?.prices || []), { model: "", input: 0, output: 0 }]);
  const removePrice = (i) => setPrices((s.ai?.prices || []).filter((_, j) => j !== i));
  const setSeo = (page, k, v) => setS((p) => ({ ...p, seo: { ...p.seo, [page]: { ...p.seo?.[page], [k]: v } } }));
  const setHero = (k, v) => setS((p) => ({ ...p, hero: { ...p.hero, [k]: v } }));

  const pillars = s.pillars || [];
  const setPillar = (i, k, v) =>
    setS((p) => ({ ...p, pillars: p.pillars.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)) }));
  const addPillar = () =>
    setS((p) => ({ ...p, pillars: [...(p.pillars || []), { icon: "Sparkles", label: "", color: "#7000FF" }] }));
  const removePillar = (i) =>
    setS((p) => ({ ...p, pillars: p.pillars.filter((_, idx) => idx !== i) }));

  const heroImgs = s.hero?.images || [];
  const uploadHero = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const room = 4 - heroImgs.length;
    if (room <= 0) { toast.error("Máximo de 4 imagens no banner."); return; }
    setUploading(true);
    try {
      const urls = [];
      for (const file of files.slice(0, room)) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(data.url);
      }
      setHero("images", [...heroImgs, ...urls]);
      toast.success(`${urls.length} imagem(ns) adicionada(s)`);
    } catch (err) { toast.error("Erro no upload"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        contacts: s.contacts, order_email: s.order_email, smtp: s.smtp,
        send_customer_confirmation: s.send_customer_confirmation !== false,
        email_templates: s.email_templates || {},
        analytics: s.analytics || {},
        hero: s.hero, seo: s.seo,
        marquee: (s.marquee || []).map((t) => t.trim()).filter(Boolean),
        pillars: (s.pillars || []).filter((p) => p.label?.trim()),
        ai: s.ai || { enabled: false, provider: "anthropic", model: "claude-haiku-4-5" },
      };
      await api.put("/admin/settings", payload);
      await refresh();
      toast.success("Definições guardadas");
    } catch (e) {
      // Say which field is wrong. The analytics ids are checked against the
      // exact shape each service issues, and "Erro ao guardar" for a mistyped
      // GA id leaves nothing to act on.
      const detail = e?.response?.data?.detail;
      const reason = Array.isArray(detail) ? detail[0]?.msg?.replace(/^Value error, /, "") : detail;
      toast.error(reason || "Erro ao guardar");
    }
    finally { setSaving(false); }
  };

  const testEmail = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/settings/test-email");
      if (data.ok) toast.success("Email de teste enviado! Verifica a caixa de entrada.");
      else toast.error(data.detail || "SMTP não configurado.");
    } catch (e) { toast.error(e.response?.data?.detail || "Falha no envio de teste"); }
    finally { setTesting(false); }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-6 gap-3">
        <h1 className="font-display font-black text-3xl tracking-tight">Definições</h1>
        <button data-testid="save-settings" onClick={save} disabled={saving}
          className="inline-flex items-center gap-2 rounded-full brand-gradient text-white font-semibold px-5 py-3 transition-transform hover:scale-105 active:scale-95 disabled:opacity-60">
          <Save size={17} /> {saving ? "A guardar..." : "Guardar tudo"}
        </button>
      </div>

      {/*
        Tabs, not sections: the panel had grown to eight cards down one page.
        Every tab edits the same object and "Guardar tudo" saves all of it, so
        switching away from a tab never loses what was typed in it.
      */}
      <div className="flex flex-wrap gap-2 mb-5">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              data-testid={`settings-tab-${t.id}`}
              aria-current={tab === t.id}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold transition-colors ${
                tab === t.id ? "bg-foreground text-white" : "border border-border hover:bg-muted"
              }`}>
              <Icon size={15} /> {t.label}
            </button>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {tab === "inicio" && (<>
        <Card icon={ImageIcon} title="Banner da Página Inicial" desc="Edita o texto e as imagens do topo da homepage (2 a 4 imagens)." className="lg:col-span-2">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Etiqueta (badge)" data-testid="set-hero-badge" value={s.hero?.badge || ""} onChange={(e) => setHero("badge", e.target.value)} />
            <div>
              <label className="text-sm font-semibold">Efeito das imagens</label>
              <select data-testid="set-hero-effect" value={s.hero?.effect || "both"} onChange={(e) => setHero("effect", e.target.value)}
                className="w-full mt-1 rounded-2xl border border-border bg-background px-4 py-3 outline-none focus:ring-2 focus:ring-primary/40">
                <option value="both">Flutuar + Parallax (recomendado)</option>
                <option value="float">Apenas flutuar</option>
                <option value="parallax">Apenas parallax (scroll)</option>
                <option value="none">Estático</option>
              </select>
            </div>
          </div>
          <Field label="Título principal" data-testid="set-hero-title" value={s.hero?.title || ""} onChange={(e) => setHero("title", e.target.value)} />
          <Field label="Destaque (texto com gradiente)" data-testid="set-hero-highlight" value={s.hero?.highlight || ""} onChange={(e) => setHero("highlight", e.target.value)} />
          <div>
            <label className="text-sm font-semibold">Subtítulo</label>
            <textarea data-testid="set-hero-subtitle" rows={2} value={s.hero?.subtitle || ""} onChange={(e) => setHero("subtitle", e.target.value)} className="w-full mt-1 rounded-2xl border border-border bg-background px-4 py-3 outline-none focus:ring-2 focus:ring-primary/40 resize-none" />
          </div>
          <div>
            <label className="text-sm font-semibold">Imagens do banner ({heroImgs.length}/4)</label>
            <div className="flex flex-wrap gap-3 mt-2">
              {heroImgs.map((im, i) => (
                <div key={i} className="relative w-24 h-24 rounded-xl overflow-hidden border border-border">
                  <img src={resolveImg(im)} alt="" className="w-full h-full object-cover" />
                  <button data-testid={`hero-img-remove-${i}`} onClick={() => setHero("images", heroImgs.filter((_, idx) => idx !== i))} className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5"><X size={13} /></button>
                </div>
              ))}
              {heroImgs.length < 4 && (
                <label data-testid="hero-upload-label" className="w-24 h-24 rounded-xl border-2 border-dashed border-border grid place-items-center cursor-pointer hover:border-primary transition-colors text-muted-foreground">
                  {uploading ? <span className="text-xs">...</span> : <Upload size={22} />}
                  <input data-testid="hero-upload-input" type="file" accept="image/*" multiple className="hidden" onChange={uploadHero} />
                </label>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-2">Podes ter entre 2 e 4 imagens. As posições ajustam-se automaticamente.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Número em destaque (ex: +120)" data-testid="set-hero-stat-value" value={s.hero?.stat_value || ""} onChange={(e) => setHero("stat_value", e.target.value)} />
            <Field label="Legenda do número" data-testid="set-hero-stat-label" value={s.hero?.stat_label || ""} onChange={(e) => setHero("stat_label", e.target.value)} />
          </div>
          <p className="text-xs text-muted-foreground">Deixa ambos vazios para esconder o cartão sobre as imagens.</p>
        </Card>

        <Card icon={Type} title="Faixa rolante" desc="A tira de texto que passa por baixo do banner.">
          <div>
            <label className="text-sm font-semibold">Termos (um por linha)</label>
            <textarea
              data-testid="set-marquee"
              rows={6}
              value={(s.marquee || []).join("\n")}
              onChange={(e) => setS((p) => ({ ...p, marquee: e.target.value.split("\n") }))}
              placeholder={"Bonecos Articulados\nDragões Flexi\nVasos Modernos"}
              className="w-full mt-1 rounded-2xl border border-border bg-background px-4 py-3 outline-none focus:ring-2 focus:ring-primary/40 resize-y font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground mt-2">
              Se deixares vazio, a faixa usa automaticamente os nomes das tuas categorias —
              assim nunca fica a anunciar categorias que já não existem.
            </p>
          </div>
        </Card>

        <Card icon={LayoutGrid} title="Pilares" desc="Os cartões com ícone logo abaixo da faixa rolante.">
          {pillars.map((p, i) => {
            const Icon = iconFor(p.icon);
            return (
              <div key={i} data-testid={`pillar-row-${i}`} className="flex flex-wrap items-end gap-3 rounded-2xl border border-border p-3">
                <span className="grid place-items-center w-11 h-11 rounded-xl shrink-0"
                  style={{ backgroundColor: `${p.color || "#7000FF"}1a`, color: p.color || "#7000FF" }}>
                  <Icon size={20} />
                </span>
                <div className="flex-1 min-w-[9rem]">
                  <label className="text-xs font-semibold">Texto</label>
                  <input data-testid={`pillar-label-${i}`} value={p.label || ""} onChange={(e) => setPillar(i, "label", e.target.value)}
                    className="w-full mt-1 rounded-xl border border-border bg-background px-3 py-2 outline-none focus:ring-2 focus:ring-primary/40" />
                </div>
                <div>
                  <label className="text-xs font-semibold">Ícone</label>
                  <select data-testid={`pillar-icon-${i}`} value={p.icon || ""} onChange={(e) => setPillar(i, "icon", e.target.value)}
                    className="mt-1 rounded-xl border border-border bg-background px-3 py-2 outline-none focus:ring-2 focus:ring-primary/40">
                    {CATEGORY_ICON_NAMES.map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold">Cor</label>
                  <input data-testid={`pillar-color-${i}`} type="color" value={p.color || "#7000FF"} onChange={(e) => setPillar(i, "color", e.target.value)}
                    className="mt-1 h-[38px] w-14 rounded-xl border border-border bg-background p-1 cursor-pointer" />
                </div>
                <button data-testid={`pillar-remove-${i}`} onClick={() => removePillar(i)} aria-label="Remover pilar"
                  className="p-2 rounded-full hover:bg-destructive/10 text-destructive"><X size={16} /></button>
              </div>
            );
          })}
          {pillars.length < MAX_PILLARS && (
            <button data-testid="pillar-add" onClick={addPillar}
              className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2.5 font-semibold hover:bg-muted transition-colors">
              <Plus size={16} /> Adicionar pilar
            </button>
          )}
          <p className="text-xs text-muted-foreground">
            Pilares sem texto são descartados ao guardar. Sem nenhum, a secção não aparece.
          </p>
        </Card>

        </>)}

        {tab === "contactos" && (<>
        <Card icon={MessageCircle} title="Contactos" desc="Aparecem no site, rodapé e botão de WhatsApp.">
          <Field label="Número WhatsApp (com indicativo, ex: 351912345678)" data-testid="set-whatsapp" value={s.contacts?.whatsapp || ""} onChange={(e) => setC("whatsapp", e.target.value)} />
          <Field label="Mensagem pré-preenchida do WhatsApp" data-testid="set-whatsapp-msg" value={s.contacts?.whatsapp_message || ""} onChange={(e) => setC("whatsapp_message", e.target.value)} />
          <Field label="Email público" data-testid="set-email" value={s.contacts?.email || ""} onChange={(e) => setC("email", e.target.value)} />
          <Field label="Instagram (handle, ex: 3duo.pt)" data-testid="set-instagram" value={s.contacts?.instagram || ""} onChange={(e) => setC("instagram", e.target.value)} />
          <Field label="Facebook (handle ou URL)" data-testid="set-facebook" value={s.contacts?.facebook || ""} onChange={(e) => setC("facebook", e.target.value)} />
        </Card>

        <Card icon={Mail} title="Email dos pedidos (SMTP)" desc="Configura o servidor de email para receberes os pedidos de orçamento na tua caixa.">
          <Field label="Email de destino dos pedidos" data-testid="set-order-email" value={s.order_email || ""} onChange={(e) => setS((p) => ({ ...p, order_email: e.target.value }))} />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Servidor SMTP (host)" data-testid="set-smtp-host" value={s.smtp?.host || ""} onChange={(e) => setSmtp("host", e.target.value)} placeholder="smtp.gmail.com" />
            <Field label="Porta" data-testid="set-smtp-port" type="number" value={s.smtp?.port || 587} onChange={(e) => setSmtp("port", parseInt(e.target.value) || 587)} />
          </div>
          <Field label="Utilizador SMTP" data-testid="set-smtp-user" value={s.smtp?.username || ""} onChange={(e) => setSmtp("username", e.target.value)} />
          <Field label="Palavra-passe SMTP" data-testid="set-smtp-pass" type="password" value={s.smtp?.password || ""} onChange={(e) => setSmtp("password", e.target.value)} />
          <Field label="Email remetente (From)" data-testid="set-smtp-from" value={s.smtp?.from_email || ""} onChange={(e) => setSmtp("from_email", e.target.value)} placeholder="pedidos@3duo.pt" />
          <label className="flex items-center gap-2 text-sm font-medium">
            <input type="checkbox" checked={!!s.smtp?.use_tls} onChange={(e) => setSmtp("use_tls", e.target.checked)} /> Usar TLS (recomendado na porta 587)
          </label>
          <label className="flex items-start gap-2 text-sm font-medium">
            <input type="checkbox" data-testid="set-customer-confirmation"
              checked={s.send_customer_confirmation !== false}
              onChange={(e) => setS((p) => ({ ...p, send_customer_confirmation: e.target.checked }))}
              className="mt-1" />
            <span>
              Enviar confirmação ao cliente
              <span className="block text-xs font-normal text-muted-foreground">
                O cliente recebe um email a dizer que o pedido chegou, com o resumo
                do que pediu. Deixa claro que é um pedido de orçamento e não uma compra.
              </span>
            </span>
          </label>
          <button data-testid="test-email-btn" onClick={testEmail} disabled={testing}
            className="inline-flex items-center gap-2 rounded-full border border-border font-semibold px-4 py-2.5 hover:bg-muted transition-colors disabled:opacity-60">
            <Send size={15} /> {testing ? "A enviar..." : "Enviar email de teste"}
          </button>
          <p className="text-xs text-muted-foreground">Guarda as definições antes de testar. Para o Gmail usa uma "palavra-passe de app".</p>
        </Card>

        <Card icon={Type} title="Texto dos emails" desc="O que é enviado quando alguém faz um pedido. Deixa um campo vazio para voltar ao texto original." className="lg:col-span-2">
          <div className="rounded-2xl border border-border bg-background p-4">
            <p className="text-sm font-semibold mb-2">Podes usar:</p>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-xs">
              {PLACEHOLDERS.map(([token, what]) => (
                <p key={token} className="text-muted-foreground">
                  <code className="font-mono text-foreground">{token}</code> — {what}
                </p>
              ))}
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-5">
            <div className="space-y-4">
              <p className="text-sm font-bold">Notificação para ti</p>
              <Field label="Assunto" data-testid="set-tpl-shop-subject"
                value={s.email_templates?.shop_subject || ""}
                onChange={(e) => setTemplate("shop_subject", e.target.value)} />
              <div>
                <label className="text-sm font-semibold">Corpo</label>
                <textarea data-testid="set-tpl-shop-body" rows={10}
                  value={s.email_templates?.shop_body || ""}
                  onChange={(e) => setTemplate("shop_body", e.target.value)}
                  className="w-full mt-1 rounded-2xl border border-border bg-background px-4 py-3 outline-none focus:ring-2 focus:ring-primary/40 resize-y font-mono text-xs" />
              </div>
            </div>

            <div className="space-y-4">
              <p className="text-sm font-bold">Confirmação para o cliente</p>
              <Field label="Assunto" data-testid="set-tpl-customer-subject"
                value={s.email_templates?.customer_subject || ""}
                onChange={(e) => setTemplate("customer_subject", e.target.value)} />
              <div>
                <label className="text-sm font-semibold">Corpo</label>
                <textarea data-testid="set-tpl-customer-body" rows={10}
                  value={s.email_templates?.customer_body || ""}
                  onChange={(e) => setTemplate("customer_body", e.target.value)}
                  className="w-full mt-1 rounded-2xl border border-border bg-background px-4 py-3 outline-none focus:ring-2 focus:ring-primary/40 resize-y font-mono text-xs" />
              </div>
            </div>
          </div>

          <p className="text-xs text-muted-foreground">
            No site nada é comprado — o carrinho pede um orçamento e tu respondes com o
            preço. Se reescreveres a confirmação, evita dar a entender que a compra
            está feita: prometia algo que ainda não foi combinado.
          </p>
        </Card>
        </>)}

        {tab === "ia" && (<>
        <Card icon={Sparkles} title="Descrições por IA" desc="Escreve a descrição de um produto a partir das fotografias." className="lg:col-span-2">
          {!ai ? (
            <p className="text-sm text-muted-foreground">A carregar...</p>
          ) : !ai.providers.anthropic && !ai.providers.openai ? (
            <div className="rounded-2xl border border-border bg-background p-4 text-sm">
              <p className="font-semibold">Falta configurar uma chave de API.</p>
              <p className="text-muted-foreground mt-1">
                A chave fica no ficheiro <code className="font-mono">backend/.env</code> do
                servidor, não aqui — é uma credencial de gasto, e assim não fica exposta
                neste painel nem num backup da base de dados. Preenche
                {" "}<code className="font-mono">ANTHROPIC_API_KEY</code> ou{" "}
                <code className="font-mono">OPENAI_API_KEY</code> e reinicia a API.
              </p>
            </div>
          ) : (
            <>
              <label className="flex items-center gap-2 text-sm font-medium">
                <input type="checkbox" data-testid="set-ai-enabled"
                  checked={!!s.ai?.enabled}
                  onChange={(e) => setS((p) => ({ ...p, ai: { ...(p.ai || {}), enabled: e.target.checked } }))} />
                Ativar a geração de descrições
              </label>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-semibold">Fornecedor</label>
                  <select data-testid="set-ai-provider" value={s.ai?.provider || "anthropic"}
                    onChange={(e) => setS((p) => ({ ...p, ai: { ...(p.ai || {}), provider: e.target.value, model: e.target.value === "anthropic" ? "claude-haiku-4-5" : "" } }))}
                    className="w-full mt-1 rounded-2xl border border-border bg-background px-4 py-3 outline-none focus:ring-2 focus:ring-primary/40">
                    <option value="anthropic" disabled={!ai.providers.anthropic}>
                      Anthropic (Claude){ai.providers.anthropic ? "" : " — sem chave"}
                    </option>
                    <option value="openai" disabled={!ai.providers.openai}>
                      OpenAI{ai.providers.openai ? "" : " — sem chave"}
                    </option>
                  </select>
                </div>

                <div>
                  <label className="text-sm font-semibold">Modelo</label>
                  {(s.ai?.provider || "anthropic") === "anthropic" ? (
                    <select data-testid="set-ai-model" value={s.ai?.model || ""}
                      onChange={(e) => setS((p) => ({ ...p, ai: { ...(p.ai || {}), model: e.target.value } }))}
                      className="w-full mt-1 rounded-2xl border border-border bg-background px-4 py-3 outline-none focus:ring-2 focus:ring-primary/40">
                      {ai.anthropic_models.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
                    </select>
                  ) : (
                    <>
                      <input data-testid="set-ai-model" value={s.ai?.model || ""}
                        onChange={(e) => setS((p) => ({ ...p, ai: { ...(p.ai || {}), model: e.target.value } }))}
                        placeholder="ex.: gpt-4o-mini"
                        className="w-full mt-1 rounded-2xl border border-border bg-background px-4 py-3 outline-none focus:ring-2 focus:ring-primary/40" />
                      <p className="text-xs text-muted-foreground mt-1">
                        Escreve o identificador exato do modelo. Confirma a lista atual em
                        platform.openai.com — tem de ser um modelo com visão.
                      </p>
                    </>
                  )}
                </div>
              </div>

              <p className="text-xs text-muted-foreground">
                No formulário de produto aparece um botão para gerar a partir de todas
                as fotos, e outro em cada foto individual. Revê sempre o texto antes de
                guardar — a IA descreve o que vê, mas pode enganar-se.
              </p>
            </>
          )}
        </Card>

        <Card icon={Coins} title="Consumo e custo" desc="Tokens contados a sério — é o que o fornecedor reporta em cada chamada." className="lg:col-span-2">
          {!spend ? (
            <p className="text-sm text-muted-foreground">A carregar...</p>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 gap-4">
                <SpendBox label="Este mês" data={spend.month} since={spend.month_started} />
                <SpendBox label="Desde sempre" data={spend.total} />
              </div>

              {spend.total.unpriced_calls > 0 && (
                <p className="text-sm rounded-2xl border border-[#c9a227]/40 bg-[#c9a227]/10 px-4 py-3">
                  <strong>{spend.total.unpriced_calls}</strong>{" "}
                  {spend.total.unpriced_calls === 1 ? "chamada usou" : "chamadas usaram"} um
                  modelo sem preço na tabela abaixo. Os tokens estão contados, mas o custo
                  real é <strong>mais alto</strong> do que o valor mostrado.
                </p>
              )}

              {spend.by_model.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                        <th className="py-2 font-semibold">Modelo</th>
                        <th className="py-2 font-semibold text-right">Chamadas</th>
                        <th className="py-2 font-semibold text-right">Tokens</th>
                        <th className="py-2 font-semibold text-right">Custo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {spend.by_model.map((row) => (
                        <tr key={row.model} className="border-t border-border">
                          <td className="py-2 font-mono text-xs">{row.model}</td>
                          <td className="py-2 text-right">
                            {row.calls}
                            {row.failed > 0 && (
                              <span className="text-muted-foreground"> ({row.failed} sem resultado)</span>
                            )}
                          </td>
                          <td className="py-2 text-right">{row.tokens.toLocaleString("pt-PT")}</td>
                          <td className="py-2 text-right font-semibold">
                            {row.unpriced_calls > 0 ? "—" : money(row.cost)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div>
                <p className="text-sm font-bold mb-1">Preços (US$ por milhão de tokens)</p>
                <p className="text-xs text-muted-foreground mb-3">
                  Os tokens são contados; o custo é uma <strong>estimativa</strong> feita
                  com estes preços. Se o fornecedor os mudar, corrige aqui — a alteração
                  só afeta chamadas novas, porque o custo de cada uma fica gravado no
                  momento em que acontece.
                </p>
                {(s.ai?.prices || []).map((p, i) => (
                  <div key={i} className="flex flex-wrap items-end gap-3 rounded-2xl border border-border p-3 mb-3">
                    <div className="flex-1 min-w-[11rem]">
                      <label className="text-xs font-semibold">Modelo</label>
                      <input data-testid={`price-model-${i}`} value={p.model || ""}
                        onChange={(e) => setPrice(i, "model", e.target.value)}
                        className="w-full mt-1 rounded-xl border border-border bg-background px-3 py-2 font-mono text-xs outline-none focus:ring-2 focus:ring-primary/40" />
                    </div>
                    <div className="w-28">
                      <label className="text-xs font-semibold">Entrada</label>
                      <input data-testid={`price-input-${i}`} type="number" step="0.01" min="0" value={p.input ?? 0}
                        onChange={(e) => setPrice(i, "input", parseFloat(e.target.value) || 0)}
                        className="w-full mt-1 rounded-xl border border-border bg-background px-3 py-2 outline-none focus:ring-2 focus:ring-primary/40" />
                    </div>
                    <div className="w-28">
                      <label className="text-xs font-semibold">Saída</label>
                      <input data-testid={`price-output-${i}`} type="number" step="0.01" min="0" value={p.output ?? 0}
                        onChange={(e) => setPrice(i, "output", parseFloat(e.target.value) || 0)}
                        className="w-full mt-1 rounded-xl border border-border bg-background px-3 py-2 outline-none focus:ring-2 focus:ring-primary/40" />
                    </div>
                    <button data-testid={`price-remove-${i}`} onClick={() => removePrice(i)} aria-label="Remover preço"
                      className="p-2 rounded-full hover:bg-destructive/10 text-destructive"><X size={16} /></button>
                  </div>
                ))}
                <button data-testid="price-add" onClick={addPrice}
                  className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2.5 font-semibold hover:bg-muted transition-colors">
                  <Plus size={16} /> Adicionar modelo
                </button>
              </div>
            </>
          )}
        </Card>
        </>)}

        {tab === "seo" && (<>
        <Card icon={Search} title="SEO — Página Inicial" desc="Título e descrição que aparecem no Google e ao partilhar.">
          <Field label="Título" data-testid="set-seo-home-title" value={s.seo?.home?.title || ""} onChange={(e) => setSeo("home", "title", e.target.value)} />
          <div>
            <label className="text-sm font-semibold">Descrição</label>
            <textarea data-testid="set-seo-home-desc" rows={2} value={s.seo?.home?.description || ""} onChange={(e) => setSeo("home", "description", e.target.value)} className="w-full mt-1 rounded-2xl border border-border bg-background px-4 py-3 outline-none focus:ring-2 focus:ring-primary/40 resize-none" />
          </div>
          <Field label="Imagem de partilha (URL)" data-testid="set-seo-home-image" value={s.seo?.home?.image || ""} onChange={(e) => setSeo("home", "image", e.target.value)} placeholder="https://..." />
        </Card>

        <Card icon={Search} title="SEO — Produtos" desc="Aplicado à página de produtos.">
          <Field label="Título" data-testid="set-seo-prod-title" value={s.seo?.products?.title || ""} onChange={(e) => setSeo("products", "title", e.target.value)} />
          <div>
            <label className="text-sm font-semibold">Descrição</label>
            <textarea data-testid="set-seo-prod-desc" rows={2} value={s.seo?.products?.description || ""} onChange={(e) => setSeo("products", "description", e.target.value)} className="w-full mt-1 rounded-2xl border border-border bg-background px-4 py-3 outline-none focus:ring-2 focus:ring-primary/40 resize-none" />
          </div>
        </Card>
        </>)}

        {tab === "analytics" && (
        <Card icon={BarChart3} title="Analytics e píxeis" desc="Preenche só o que usas. Com os campos vazios, o site não carrega nada nem cria cookies de terceiros." className="lg:col-span-2">
          <div className="grid md:grid-cols-3 gap-4">
            <Field label="Google Analytics (GA4)" data-testid="set-ga4"
              value={s.analytics?.ga4_id || ""}
              onChange={(e) => setAnalytics("ga4_id", e.target.value)} placeholder="G-ABC1234567" />
            <Field label="Google Tag Manager" data-testid="set-gtm"
              value={s.analytics?.gtm_id || ""}
              onChange={(e) => setAnalytics("gtm_id", e.target.value)} placeholder="GTM-ABC1234" />
            <Field label="Pixel da Meta (Facebook)" data-testid="set-pixel"
              value={s.analytics?.meta_pixel_id || ""}
              onChange={(e) => setAnalytics("meta_pixel_id", e.target.value)} placeholder="123456789012345" />
          </div>

          <div className="rounded-2xl border border-[#c9a227]/40 bg-[#c9a227]/10 p-4 text-sm">
            <p className="font-semibold flex items-center gap-2">
              <AlertTriangle size={16} /> Consentimento de cookies
            </p>
            <p className="text-muted-foreground mt-1">
              O Google Analytics e o Pixel da Meta guardam cookies e tratam dados pessoais.
              Na UE precisas do consentimento do visitante <strong>antes</strong> de os
              carregar, e o site ainda não tem esse aviso. Enquanto o não tiver, ativar
              estes campos deixa-te fora das regras do RGPD. Pede-me o banner de
              consentimento e eu ligo-o a isto.
            </p>
          </div>

          <div>
            <label className="text-sm font-semibold">Outros scripts (avançado)</label>
            <textarea data-testid="set-custom-head" rows={6}
              value={s.analytics?.custom_head || ""}
              onChange={(e) => setAnalytics("custom_head", e.target.value)}
              placeholder="<!-- Cole aqui outro script de medição, ex.: TikTok, Clarity -->"
              className="w-full mt-1 rounded-2xl border border-border bg-background px-4 py-3 outline-none focus:ring-2 focus:ring-primary/40 resize-y font-mono text-xs" />
            <p className="text-xs text-muted-foreground mt-2">
              Os três campos acima só aceitam o formato exato do identificador de cada
              serviço, por isso não podem conter mais nada. Este não é verificado — o que
              puseres aqui corre em <strong>todas as páginas</strong>, para todos os
              visitantes. Cola só código que venha do próprio serviço.
            </p>
          </div>
        </Card>
        )}
      </div>
    </>
  );
};
